import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import Text from 'particles/Text'
import Label, { LabelSize, LinkConfig } from 'particles/Label'
import styled from 'particles/themes/styled'
import { DropzoneRootProps, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { parseFileSize, convertToBytes } from 'particles/utils'
import IconButton from 'particles/Button/IconButton'
import Tooltip from 'particles/Tooltip'
import { buildMediaUrl, ContentData } from 'services/cloudinary'
import CommentContainer from 'components/Shared/Comment'
import Icon from 'particles/Icon'
import Preview from './Preview'

export type FileUploaderSize = 's' | 'm'

interface FileError {
    inlineError: boolean
    message?: string
}

export interface FileState extends File, ContentData {
    preview?: string
    progress?: number // 0-100
}

interface LocalFile {
    file: FileState
    error: string | null
    uuid: string
}

interface SelectedFilesProps {
    files: LocalFile[]
    errors?: (FileError | undefined)[]
    onRemoveFile?: (file: FileState, index: number, isUploaded: boolean, uuid?: string) => void
    onClickThumbnail?: (file: FileState) => void
    isSingleFileOnly?: boolean
    isDisabled?: boolean
    isReadOnly?: boolean
    dataTestId?: string
    isUploaded: boolean
    fileUploadProgress?: Record<string, number>
}

const SelectedFiles: React.FC<SelectedFilesProps> = ({
    files,
    errors,
    onRemoveFile,
    onClickThumbnail,
    isSingleFileOnly,
    isDisabled,
    isReadOnly,
    dataTestId,
    isUploaded,
    fileUploadProgress,
}) => {
    const handleDownload = (file: FileState) => {
        // S3 records carry a complete URL (already ends in .pdf); the attachment flag and
        // extension suffix are Cloudinary transformations that only apply to legacy assets
        const pdfUrl =
            file.source === 'aws'
                ? buildMediaUrl(file)
                : buildMediaUrl(
                      file,
                      {
                          flag: 'attachment',
                      },
                      false,
                  ) + '.pdf'

        const link = document.createElement('a')
        link.href = pdfUrl

        // download with the file name
        link.setAttribute('download', file.name || `document_${new Date().getTime()}.pdf`)

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const handleClick = (file: FileState) => {
        if (!isUploaded) return
        if (file.file_format === 'pdf') {
            return handleDownload(file)
        }

        onClickThumbnail?.(file)
    }
    return (
        <div
            className={
                isSingleFileOnly && !Object.keys(fileUploadProgress || {}).length ? 'selected-file' : 'selected-files'
            }
        >
            {files?.map((preview, index: number) => {
                const file = preview.file
                const error = preview.error
                return (
                    <div key={index} className="thumb-container" onClick={() => handleClick(file)}>
                        <div
                            className={`thumb ${
                                errors && errors[index] && (errors[index] as FileError).inlineError && 'error'
                            }`}
                            data-testid={`${dataTestId}-thumbnail-${index}`}
                        >
                            {onRemoveFile && !isDisabled && !isReadOnly ? (
                                <IconButton
                                    form="filled"
                                    size="xs"
                                    color="neutral"
                                    type="button"
                                    icon="x"
                                    className="remove-button"
                                    onClick={e => {
                                        e.stopPropagation()
                                        onRemoveFile(file, index, !!file.public_id, preview.uuid)
                                    }}
                                />
                            ) : null}

                            {/* PDFs first: uploaded records report resource_type 'image' with file_format
                                'pdf', and S3 cannot rasterise a page the way Cloudinary's f_auto did */}
                            {file?.file_format === 'pdf' || file?.type === 'application/pdf' ? (
                                <PDFThumbnail>
                                    <Icon name="filePdf" size="24px" />
                                    <Text scale="caption500" className="file-name">
                                        {file.name || file.public_id?.split('/').pop()}
                                    </Text>
                                </PDFThumbnail>
                            ) : file?.resource_type === 'image' || file?.type?.startsWith('image') ? (
                                <img
                                    className="media"
                                    alt={file.name}
                                    src={file.preview ? file.preview : buildMediaUrl(file)}
                                />
                            ) : file?.resource_type === 'video' || file?.type?.startsWith('video') ? (
                                <video className="media" controls={false}>
                                    <source src={file.preview ? file.preview : buildMediaUrl(file)} />
                                </video>
                            ) : null}

                            {fileUploadProgress?.[file.name] !== undefined && fileUploadProgress?.[file.name] < 100 && (
                                <ProgressBar>
                                    <Progress style={{ width: `${fileUploadProgress?.[file.name]}%` }} />
                                </ProgressBar>
                            )}
                        </div>
                        {error ? (
                            <Text scale="caption400" color="error">
                                {error}
                            </Text>
                        ) : null}
                    </div>
                )
            })}
        </div>
    )
}

const getFileRatioErrorMessage = (t: TFunction): FileError => ({
    inlineError: true,
    message: t('errorMessage.general.uploadImageCorrectSize'),
})

const getFileTooLargeErrorMessage = (t: TFunction, imageLimit: number, videoLimit: number): FileError => ({
    inlineError: true,
    message: t('errorMessage.general.uploadSmallerPhotosVideos', {
        imageLimit: parseFileSize(imageLimit),
        videoLimit: parseFileSize(videoLimit),
    }),
})

export type AcceptType = 'image' | 'video' | 'pdf'

const mimeTypeMap: Record<AcceptType, string> = {
    image: 'image/*',
    video: 'video/*',
    pdf: 'application/pdf',
}

interface FileUploaderProps {
    className?: string
    size?: FileUploaderSize
    isReadOnly?: boolean
    readOnlyEmptyText?: string
    isDisabled?: boolean
    tooltipContext?: { title?: string; label?: string }
    errorMessage?: string
    label?: string
    subtext?: string
    isRequired?: boolean
    isCommentable?: boolean
    isCommented?: boolean
    placeholder?: string
    onLabelClick?: (e: React.MouseEvent<HTMLDivElement>) => void
    maxFiles?: number
    width?: number | string
    imageLimitInMB?: number
    videoLimitInMB?: number
    requiredWidth?: number
    requiredHeight?: number
    acceptVideo?: boolean
    acceptTypes?: AcceptType[]
    isLoading?: boolean
    onUpload?: (files: FileState[]) => void
    uploadedFiles?: FileState[]
    onRemoveUploadedFile?: (file: FileState, index: number) => void
    uploadingFiles?: FileState[]
    selectedFilesErrors?: (FileError | undefined)[]
    uploadContainerWidth?: number | string
    identifier?: string
    dataTestId?: string
    labelSize?: LabelSize
    link?: LinkConfig
    labelTooltip?: string
    fileUploadProgress?: Record<string, number>
}

export interface FileUploaderHandle {
    resetFileErrors: () => void
}

const FileUploader = forwardRef<FileUploaderHandle, FileUploaderProps>(
    (
        {
            className,
            label = '',
            subtext,
            placeholder,
            tooltipContext,
            isDisabled = false,
            isReadOnly = false,
            readOnlyEmptyText,
            isRequired = false,
            isCommentable = false,
            isCommented = false,
            errorMessage,
            size = 'm',
            onLabelClick,
            maxFiles = 1,
            // Size limit for image and video
            imageLimitInMB,
            videoLimitInMB,
            // This will be the required File ratio
            requiredWidth,
            requiredHeight,
            acceptVideo,
            acceptTypes,
            // This will control button loading state
            isLoading,
            // Props to control the upload
            onUpload,
            uploadedFiles,
            onRemoveUploadedFile,
            // To allow parent component to update the state inside
            uploadingFiles,
            selectedFilesErrors,
            uploadContainerWidth,
            identifier,
            dataTestId,
            labelSize,
            link,
            labelTooltip,
            fileUploadProgress,
        },
        ref,
    ) => {
        const [t] = useTranslation()
        const [_errors, setErrors] = useState<(FileError | undefined)[]>(selectedFilesErrors ?? [])
        const [previewMediaData, setPreviewMediaData] = useState<FileState | null>(null)
        const [isMaxFilesReached, setIsMaxFilesReached] = useState(false)
        const [errorFiles, setErrorFiles] = useState<LocalFile[]>([])

        const mimeType = useMemo(() => {
            return acceptTypes?.map(value => mimeTypeMap[value])?.join(', ')
        }, [acceptTypes])

        useImperativeHandle(ref, () => ({
            resetFileErrors: () => {
                setIsMaxFilesReached(false)
                setErrorFiles([])
            },
        }))

        useEffect(() => {
            const uploadedLength = uploadedFiles?.length
            if (!!uploadedLength && uploadedLength <= maxFiles) {
                setIsMaxFilesReached(false)
            }
        }, [uploadedFiles])

        const onDrop = (files: File[]): void => {
            setIsMaxFilesReached(false)
            const errors = files.map(async file => {
                if (
                    (imageLimitInMB && file.type.indexOf('image') > -1 && file.size > convertToBytes(imageLimitInMB)) ||
                    (videoLimitInMB && file.type.indexOf('video') > -1 && file.size > convertToBytes(videoLimitInMB))
                )
                    return getFileTooLargeErrorMessage(t, imageLimitInMB ?? 0, videoLimitInMB ?? 0)
                if (requiredWidth && requiredHeight) {
                    const img = new Image()
                    img.src = URL.createObjectURL(file)
                    await img.decode()
                    const expectHeight = (requiredHeight / requiredWidth) * img.width
                    if (img.height < Math.floor(expectHeight) - 10 || img.height > Math.ceil(expectHeight) + 10)
                        return getFileRatioErrorMessage(t)
                }
                return undefined
            })

            if (uploadedFiles?.length === maxFiles) {
                setIsMaxFilesReached(true)
                return
            }

            Promise.all(errors).then(errors => {
                const filteredErrors = errors.filter(e => !!e)
                const selectedFiles = files.map(
                    file =>
                        Object.assign(file, {
                            preview: URL.createObjectURL(file),
                        }) as FileState,
                )

                if (filteredErrors.length) {
                    filteredErrors.forEach(error => {
                        if (error?.inlineError) {
                            setErrorFiles(
                                prev =>
                                    [
                                        ...prev,
                                        {
                                            uuid: crypto.randomUUID(),
                                            file: selectedFiles[0],
                                            error: error.message,
                                        },
                                    ] as LocalFile[],
                            )
                        }
                    })
                    return
                }
                onUpload?.(selectedFiles)
            })
        }

        const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
            accept: mimeType,
            onDrop,
            multiple: maxFiles > 1,
            disabled: isDisabled,
        })

        useEffect(() => {
            if (selectedFilesErrors) {
                setErrors(selectedFilesErrors)
            }
        }, [selectedFilesErrors])

        const showErrorMessage = Boolean(!isReadOnly && !isDisabled && errorMessage) || isMaxFilesReached

        const isSingleFileOnly = maxFiles === 1

        const isUploadedFilesEmpty = uploadedFiles && uploadedFiles.length === 0

        const showUploadContainer = isDisabled
            ? isSingleFileOnly
                ? isUploadedFilesEmpty
                : isUploadedFilesEmpty
            : isSingleFileOnly
              ? isUploadedFilesEmpty
              : true

        const onRemove = (file: FileState, index: number, isUploaded: boolean, uuid?: string) => {
            onRemoveUploadedFile?.(file, index)
            if (!isUploaded) {
                setErrorFiles(errFiles => errFiles.filter(val => val.uuid !== uuid))
            }
        }

        const previewFiles: LocalFile[] = useMemo(() => {
            const preview: LocalFile[] = []
            const displayFiles = [...(uploadedFiles || []), ...(uploadingFiles || [])]
            displayFiles?.forEach(file => {
                preview.push({
                    file,
                    error: null,
                    uuid: file.uuid || crypto.randomUUID(),
                })
            })

            errorFiles?.forEach(file => {
                preview.push({
                    file: file.file,
                    error: file.error,
                    uuid: file.uuid,
                })
            })

            return preview
        }, [errorFiles, uploadedFiles, uploadingFiles])

        return (
            <div className={className}>
                {!!previewMediaData && <Preview media={previewMediaData} onClose={() => setPreviewMediaData(null)} />}
                {(label || subtext) && (
                    <CommentContainer identifier={identifier}>
                        <Label
                            title={label}
                            subtext={subtext}
                            isRequired={isRequired}
                            size={labelSize ? labelSize : size}
                            isCommentable={isCommentable}
                            isCommented={isCommented}
                            onClick={onLabelClick}
                            link={link}
                            tooltip={labelTooltip}
                        />
                    </CommentContainer>
                )}
                <div className="container-with-error-message">
                    <SelectedFiles
                        isSingleFileOnly={isSingleFileOnly}
                        files={previewFiles}
                        fileUploadProgress={fileUploadProgress}
                        onRemoveFile={!isReadOnly && !isDisabled ? onRemove : undefined}
                        isDisabled={isDisabled}
                        isReadOnly={isReadOnly}
                        dataTestId={dataTestId}
                        onClickThumbnail={media => setPreviewMediaData(media)}
                        isUploaded
                    />
                    {isReadOnly
                        ? isUploadedFilesEmpty && (
                              <div className="readonly-empty">
                                  <Text color="disable" scale="bodyS400">
                                      {readOnlyEmptyText
                                          ? readOnlyEmptyText
                                          : t('placeholderText.imageUploader.noFile')}
                                  </Text>
                              </div>
                          )
                        : showUploadContainer && (
                              <UploadContainer
                                  {...getRootProps()}
                                  isError={showErrorMessage}
                                  size={size}
                                  width={uploadContainerWidth}
                                  isDisabled={isDisabled}
                                  isDragActive={isDragActive}
                                  isDragReject={isDragReject}
                                  data-testid={`${dataTestId}-container`}
                              >
                                  <input {...getInputProps()} />
                                  <Icon
                                      name="fileArrowUp"
                                      color={
                                          isDisabled
                                              ? 'disable'
                                              : showErrorMessage
                                                ? 'error'
                                                : isDragActive
                                                  ? 'actionActive'
                                                  : 'action'
                                      }
                                      size="24px"
                                  />
                                  <Text
                                      className="placeholder"
                                      scale="caption400"
                                      color={
                                          isDisabled
                                              ? 'disable'
                                              : showErrorMessage
                                                ? 'error'
                                                : isDragActive
                                                  ? 'actionActive'
                                                  : 'action'
                                      }
                                  >
                                      {placeholder ? placeholder : t('placeholderText.dragDropFiles')}
                                  </Text>
                                  {isDisabled && (tooltipContext?.label || tooltipContext?.title) && (
                                      <Tooltip {...tooltipContext} onClick={e => e.stopPropagation()}>
                                          <IconButton
                                              size="xs"
                                              color="neutral"
                                              form="icon"
                                              icon="question"
                                              className="tooltip"
                                          />
                                      </Tooltip>
                                  )}
                              </UploadContainer>
                          )}
                    {showErrorMessage && (
                        <Text scale="caption400" color="error">
                            {isMaxFilesReached ? t('errorMessage.validation.maximumFiles', { maxFiles }) : errorMessage}
                        </Text>
                    )}
                </div>
            </div>
        )
    },
)

FileUploader.displayName = 'FileUploader'

export default styled(FileUploader)`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[200]};
    .container-with-error-message {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[100]};
        .readonly-empty {
            display: flex;
            padding: 10px 11px;
            align-items: center;
            gap: ${({ theme }) => theme.spacing[200]};
            border-radius: 4px;
            border-left: 1px solid ${({ theme }) => theme.colors.border.primary};
        }
    }
    .container-with-error-message .selected-file,
    .selected-file {
        display: flex;
        flex-wrap: wrap;
        .thumb-container {
            position: relative;
            .thumb .remove-button {
                position: absolute;
                right: 5px;
                top: 5px;
                opacity: 0.8;
                z-index: 1;
            }
            .thumb .media {
                box-sizing: border-box;
                width: ${({ width }) => (width ? (typeof width === 'number' ? `${width}px` : width) : '343px')};
                border-radius: 4px;
                background-color: ${({ theme }) => theme.colors.surface.tertiary};
                &.error {
                    border: 1px solid ${({ theme }) => theme.colors.border.error};
                }
            }
        }
    }
    .container-with-error-message .selected-files,
    .selected-files {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        gap: ${({ theme }) => theme.spacing[300]};
        .thumb-container {
            display: flex;
            flex-wrap: wrap;
            width: 104px;
            gap: ${({ theme }) => theme.spacing[100]};
            .thumb {
                box-sizing: border-box;
                width: 104px;
                height: 84px;
                border-radius: 4px;
                padding: 12px;
                background-color: ${({ theme }) => theme.colors.surface.tertiary};
                position: relative;
                &.error {
                    border: 1px solid ${({ theme }) => theme.colors.border.error};
                }
                .media {
                    height: 60px;
                    width: 80px;
                    border-radius: 4px;
                    object-fit: contain;
                }
                .remove-button {
                    position: absolute;
                    right: 5px;
                    top: 5px;
                    opacity: 0.8;
                    z-index: 1;
                }
            }
        }
    }
`

interface UploadContainerProps extends DropzoneRootProps {
    isError?: boolean
    isDragActive: boolean
    isDragReject: boolean
    isDisabled: boolean
    size?: FileUploaderSize
}

const UploadContainer = styled.div<UploadContainerProps>`
    box-sizing: border-box;
    height: ${({ size }) => (size === 's' ? '80px' : '100px')};
    width: ${({ width }) => (width ? (typeof width === 'number' ? `${width}px` : width) : '343px')};
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[100]};
    align-items: center;
    justify-content: center;
    padding: 20px;
    cursor: pointer;
    border: 1px dashed
        ${({ theme, isError, isDisabled, isDragActive }) =>
            isError
                ? theme.colors.border.error
                : isDragActive
                  ? theme.colors.border.actionActive
                  : !isDisabled
                    ? theme.colors.border.action
                    : theme.colors.border.primary};
    dashes: 4, 6;
    border-radius: 4px;
    opacity: ${({ isDisabled }) => (isDisabled ? 0.6 : 1)};
    position: relative;
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
    .placeholder {
        opacity: ${({ isDisabled }) => (isDisabled ? 0.6 : 1)};
    }
    outline: none;
    @media screen and (max-width: 768px) {
        width: 100%;
    }
    &.disable {
        cursor: default;
    }
    .tooltip {
        position: absolute;
        top: 7px;
        right: 7px;
    }
`

const PDFThumbnail = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: center;
    justify-content: center;
    background: white;
    border-radius: 4px;
    height: 60px;
    padding: 2px;

    .file-name {
        text-align: center;
        max-width: 100%;
        word-break: break-all;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 2;
        display: -webkit-box;
        -webkit-box-orient: vertical;
    }
`

const ProgressBar = styled.div`
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    height: 4px;
    border-radius: 2px;
    overflow: hidden;
    width: calc(100% - 35px);
    background: ${({ theme }) => theme.colors.surface.tertiary};
`

const Progress = styled.div`
    height: 100%;
    background: ${({ theme }) => theme.colors.surface.action};
    transition: width 0.2s ease;
`
